import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  InputAdornment,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { ReactComponent as FacebookAdsIcon } from 'src/icons/facebook.svg';
import { ReactComponent as GoogleAdsIcon } from 'src/icons/googleAds.svg';
import { ReactComponent as GoogleAnalyticsIcon } from 'src/icons/googleAnalytics.svg';
import { ReactComponent as GoogleSearchConsoleIcon } from 'src/icons/googleSearchConsole.svg';
import { ReactComponent as ShopifyIcon } from 'src/icons/shopify.svg';
import kipifyLogo from 'src/icons/kipify-logo.png';
import {
  ConnectorType,
  Metric,
  MetricsByConnectorType,
} from 'src/utils/gql/types';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    maxHeight: '85vh',
    minWidth: '500px',
    [theme.breakpoints.down('sm')]: {
      minWidth: '90vw',
      maxHeight: '90vh',
    },
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(3, 3, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .MuiTypography-root': {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
}));

const SearchField = styled(TextField)(({ theme }) => ({
  marginTop: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
    '&.Mui-focused': {
      backgroundColor: 'white',
    },
    '& .MuiOutlinedInput-input': {
      padding: theme.spacing(1.5, 1.5, 1.5, 0),
    },
  },
}));

const ConnectorHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.grey[50],
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: 'sticky',
  top: 0,
  zIndex: 1,
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.875rem',
  height: '28px',
  '& .MuiChip-label': {
    padding: theme.spacing(0, 1.5),
  },
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: 0,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(0.5),
  margin: theme.spacing(0.25, 1),
  '&:hover': {
    backgroundColor: theme.palette.primary.light + '15',
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.light + '25',
    '&:hover': {
      backgroundColor: theme.palette.primary.light + '35',
    },
  },
}));

const MetricIconWrapper = styled(Box)(() => ({
  width: 32,
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& svg': {
    width: '100%',
    height: '100%',
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: 0,
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(30, 237, 159, 0.1)',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(30, 237, 159, 0.6)',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: 'rgba(30, 237, 159, 0.8)',
    },
  },
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

interface MetricSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelectMetric: (
    metric: Metric,
    connectorType: ConnectorType | string,
  ) => void;
  availableMetrics: Array<{
    metric: Metric;
    connectorType: ConnectorType | string;
  }>;
}

export const MetricSelector: React.FC<MetricSelectorProps> = ({
  open,
  onClose,
  onSelectMetric,
  availableMetrics,
}) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredMetrics = useMemo(() => {
    if (!searchTerm.trim()) return availableMetrics;

    const lowerSearch = searchTerm.toLowerCase();
    return availableMetrics.filter(
      ({ metric }) =>
        metric.label.toLowerCase().includes(lowerSearch) ||
        metric.key.toLowerCase().includes(lowerSearch),
    );
  }, [availableMetrics, searchTerm]);

  const groupedMetrics = useMemo(() => {
    const grouped: Record<
      string,
      Array<{ metric: Metric; connectorType: ConnectorType | string }>
    > = {};
    filteredMetrics.forEach((item) => {
      const key = item.connectorType;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(item);
    });

    // Ordina i gruppi: Kipify per primo
    const orderedGroups: Record<
      string,
      Array<{ metric: Metric; connectorType: ConnectorType | string }>
    > = {};
    const order = [
      MetricsByConnectorType.KIPIFY,
      ConnectorType.SHOPIFY,
      ConnectorType.FACEBOOK,
      ConnectorType.GOOGLE_ADS,
      ConnectorType.GOOGlE_ANALYTICS4,
      ConnectorType.DATABASE,
    ];

    // Aggiungi prima Kipify e gli altri nell'ordine specificato
    order.forEach((connectorType) => {
      if (grouped[connectorType]) {
        orderedGroups[connectorType] = grouped[connectorType];
      }
    });

    // Aggiungi eventuali altri connettori non nell'ordine
    Object.keys(grouped).forEach((key) => {
      if (!order.includes(key as any)) {
        orderedGroups[key] = grouped[key];
      }
    });

    return orderedGroups;
  }, [filteredMetrics]);

  const getConnectorLabel = (connectorType: ConnectorType | string): string => {
    const labels: Record<string, string> = {
      [ConnectorType.SHOPIFY]: 'Shopify',
      [ConnectorType.FACEBOOK]: 'Facebook Ads',
      [ConnectorType.GOOGLE_ADS]: 'Google Ads',
      [ConnectorType.GOOGlE_ANALYTICS4]: 'Google Analytics',
      [ConnectorType.GOOGLE_SEARCH_CONSOLE]: 'Google Search Console',
      [MetricsByConnectorType.KIPIFY]: 'Kipify',
      [ConnectorType.DATABASE]: 'Database',
      [ConnectorType.MAGENTO]: 'Magento',
    };
    return labels[connectorType] || connectorType;
  };

  const getConnectorColor = (connectorType: ConnectorType | string): string => {
    const colors: Record<string, string> = {
      [ConnectorType.SHOPIFY]: '#95bf47',
      [ConnectorType.FACEBOOK]: '#1877f2',
      [ConnectorType.GOOGLE_ADS]: '#4285f4',
      [ConnectorType.GOOGlE_ANALYTICS4]: '#fbbc04',
      [ConnectorType.GOOGLE_SEARCH_CONSOLE]: '#4285F4',
      [MetricsByConnectorType.KIPIFY]: '#1EED9F',
      [ConnectorType.DATABASE]: '#1976d2',
      [ConnectorType.MAGENTO]: '#ee6723',
    };
    return colors[connectorType] || '#667eea';
  };

  const getConnectorIcon = (connectorType: ConnectorType | string) => {
    switch (connectorType) {
      case ConnectorType.SHOPIFY:
        return <ShopifyIcon />;
      case ConnectorType.FACEBOOK:
        return <FacebookAdsIcon />;
      case ConnectorType.GOOGLE_ADS:
        return <GoogleAdsIcon />;
      case ConnectorType.GOOGlE_ANALYTICS4:
        return <GoogleAnalyticsIcon />;
      case ConnectorType.GOOGLE_SEARCH_CONSOLE:
        return <GoogleSearchConsoleIcon />;
      case MetricsByConnectorType.KIPIFY:
        return (
          <img
            src={kipifyLogo}
            alt="Kipify"
            style={{
              width: '32px',
              height: '32px',
              objectFit: 'contain',
            }}
          />
        );
      case ConnectorType.DATABASE:
        return (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: '#1976d2',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            DB
          </Box>
        );
      case ConnectorType.MAGENTO:
        return (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: '#ee6723',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            M
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        Select Metric
        <SearchField
          fullWidth
          placeholder="Search metrics..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: 1.5 }}>
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
      </StyledDialogTitle>
      <StyledDialogContent dividers>
        {availableMetrics.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              No metrics available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please configure at least one connector to see available metrics.
            </Typography>
          </Box>
        ) : filteredMetrics.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              No metrics found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try a different search term.
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 1 }}>
            {Object.entries(groupedMetrics).map(
              ([connectorType, metrics], groupIndex) => (
                <Box key={connectorType}>
                  <ConnectorHeader>
                    <StyledChip
                      label={getConnectorLabel(connectorType)}
                      sx={{
                        backgroundColor: getConnectorColor(connectorType),
                        color: 'white',
                      }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      {metrics.length} metric{metrics.length !== 1 ? 's' : ''}
                    </Typography>
                  </ConnectorHeader>
                  {metrics.map(({ metric }, index) => (
                    <StyledListItem
                      key={`${connectorType}-${metric.key}-${index}`}
                      disablePadding
                    >
                      <StyledListItemButton
                        onClick={() => {
                          onSelectMetric(metric, connectorType);
                          onClose();
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 48 }}>
                          <MetricIconWrapper>
                            {getConnectorIcon(connectorType)}
                          </MetricIconWrapper>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body1"
                              fontWeight={500}
                              color="text.primary"
                            >
                              {metric.label}
                            </Typography>
                          }
                        />
                      </StyledListItemButton>
                    </StyledListItem>
                  ))}
                  {groupIndex < Object.keys(groupedMetrics).length - 1 && (
                    <Divider sx={{ my: 1 }} />
                  )}
                </Box>
              ),
            )}
          </List>
        )}
      </StyledDialogContent>
      <StyledDialogActions>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            minWidth: 100,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Cancel
        </Button>
      </StyledDialogActions>
    </StyledDialog>
  );
};
